import React from 'react';
import { FolderData } from "../../types/PanoptoData";
import { fetchPanoptoSessions } from '../../services/api/panoptoService';
import PanoptoTree from './PanoptoTree';

interface PanoptoPreviewTabProps {
  sortedFolderData: FolderData | undefined;
  updateExcludedItems: (folderId: number, uncheckedIds: string[]) => void;
  triggerAutoSave: () => void;
  setPendingSave: React.Dispatch<React.SetStateAction<boolean>>;
  setPanoptos: React.Dispatch<React.SetStateAction<FolderData[]>>;
  handleUncheckedItems: (folderId: number) => (uncheckedItemIds: string[]) => void;
}

const PanoptoPreviewTab: React.FC<PanoptoPreviewTabProps> = ({
  sortedFolderData,
  updateExcludedItems,
  triggerAutoSave,
  setPendingSave,
  setPanoptos,
  handleUncheckedItems
}) => {
  
  const fetchPanoptoItemsOnRefresh = async () => {
    if (!sortedFolderData?.panopto_id) return;
    
    try {
      setPanoptos(prev => prev.map(c => 
        c.id === sortedFolderData?.id ? { ...c, isLoadingItems: true } : c
      ));
      
      const sessions = await fetchPanoptoSessions(sortedFolderData.panopto_id);
      
      setPanoptos(prev => prev.map(c => 
        c.id === sortedFolderData?.id
          ? { 
              ...c, 
              panoptoItems: {
                ...c.panoptoItems,
                files: sessions || [],
              },
              isLoadingItems: false,
              error: undefined,
              excludedItems: c.excludedItems || [], 
            } 
          : c
      ));
      
    } catch (e) {
      console.error("Error fetching Panopto data", e);
      setPanoptos(prev => prev.map(c => 
        c.id === sortedFolderData?.id 
          ? { ...c, isLoadingItems: false, error: 'Failed to fetch items. Please try again.' } 
          : c
      ));
    }
  };

  if (!sortedFolderData) {
    return (
      <div className="text-center py-4 text-gray-500">
        No folder selected
      </div>
    );
  }

  return (
    <div className="">
      <div className="overflow-y-auto">
        {sortedFolderData.isLoadingItems ? (
          <div className="flex justify-center items-center pl-[15%] pt-[15%]">
            <div className="dotted-circle">
              {[...Array(10)].map((_, i) => (
                <span key={i} className="dot" style={{ "--i": i } as React.CSSProperties}></span>
              ))}
            </div>
          </div>
        ) : sortedFolderData.error ? (
          <div className="text-red-600 text-center py-4">
            {sortedFolderData.error}
          </div>
        ) : (
          <div>
            <PanoptoTree
              data={sortedFolderData?.panoptoItems?.files || []}
              uncheckedItemIds={sortedFolderData.excludedItems}
              onUncheckedItems={handleUncheckedItems(sortedFolderData?.id)}
              onRefresh={fetchPanoptoItemsOnRefresh}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PanoptoPreviewTab;